import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { logout } from '../services/api';
import { useLanguage } from '../contexts/LanguageContext';
import { syncTableDataLabels } from '../utils/syncTableDataLabels';
import {
  Home,
  FileText,
  Users,
  TrendingUp,
  BarChart3,
  Settings,
  Plus,
  LogOut,
  Package,
  Headset,
  Menu,
  X,
  ChevronRight
} from 'lucide-react';
import SidebarWave from './SidebarWave';

const Layout = ({ children }) => {
  const { t, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [, forceRefresh] = useState(0);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [headerHidden, setHeaderHidden] = useState(false);
  const contentRef = useRef(null);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);

  // Listen for localStorage changes to update business name
  useEffect(() => {
    const handleStorageChange = () => {
      forceRefresh(prev => prev + 1);
    };

    // Listen for storage events (from other tabs/windows)
    window.addEventListener('storage', handleStorageChange);
    
    // Also listen for custom events for same-tab updates
    const handleCustomStorageChange = () => {
      forceRefresh(prev => prev + 1);
    };
    window.addEventListener('localStorageUpdated', handleCustomStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('localStorageUpdated', handleCustomStorageChange);
    };
  }, []);

  // Close sidebar on route change (mobile) + show header again
  useEffect(() => {
    setSidebarOpen(false);
    setHeaderHidden(false);
    lastScrollY.current = window.scrollY || 0;
  }, [location.pathname]);

  // Hide header on any scroll down; show on any scroll up
  useEffect(() => {
    const layoutEl = document.querySelector('.layout');
    const mainEl = document.querySelector('.main-content');
    const contentEl = contentRef.current;
    const targets = [window, document, layoutEl, mainEl, contentEl].filter(Boolean);

    const getScrollY = () => {
      const values = [
        window.scrollY || 0,
        document.documentElement.scrollTop || 0,
        document.body.scrollTop || 0,
        layoutEl ? layoutEl.scrollTop : 0,
        mainEl ? mainEl.scrollTop : 0,
        contentEl ? contentEl.scrollTop : 0
      ];
      return Math.max(...values);
    };

    const updateHeader = () => {
      const currentY = getScrollY();
      const delta = currentY - lastScrollY.current;

      if (currentY <= 2) {
        setHeaderHidden(false);
      } else if (delta > 0) {
        // scrolling down at any point → hide
        setHeaderHidden(true);
      } else if (delta < 0) {
        // scrolling up at any point → show
        setHeaderHidden(false);
      }

      lastScrollY.current = currentY;
      ticking.current = false;
    };

    const onScroll = () => {
      if (!ticking.current) {
        ticking.current = true;
        window.requestAnimationFrame(updateHeader);
      }
    };

    lastScrollY.current = getScrollY();
    targets.forEach((target) => {
      target.addEventListener('scroll', onScroll, { passive: true });
    });

    return () => {
      targets.forEach((target) => {
        target.removeEventListener('scroll', onScroll);
      });
    };
  }, []);

  // Close sidebar drawer when resizing to desktop
  useEffect(() => {
    const handleResize = () => {
      if (window.innerWidth > 1024) {
        setSidebarOpen(false);
        document.body.style.overflow = '';
      }
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // When sidebar opens, keep header visible and lock body scroll
  useEffect(() => {
    if (sidebarOpen) {
      setHeaderHidden(false);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [sidebarOpen]);

  // Keep data-label on table cells in sync with headers (for CSS card layout)
  useEffect(() => {
    const root = contentRef.current;
    if (!root) return undefined;

    let frameId = 0;
    const runSync = () => {
      cancelAnimationFrame(frameId);
      frameId = requestAnimationFrame(() => syncTableDataLabels(root));
    };

    runSync();

    const observer = new MutationObserver(runSync);
    observer.observe(root, {
      childList: true,
      subtree: true,
      characterData: true
    });

    return () => {
      cancelAnimationFrame(frameId);
      observer.disconnect();
    };
  }, [location.pathname, language, children]);

  const handleLogout = async () => {
    await logout();
    navigate('/login?logout=success');
  };
  
  const navItems = [
    { path: '/dashboard', icon: Home, label: t('nav.dashboard'), iconColor: 'purple' },
    { path: '/loans', icon: FileText, label: t('nav.loans'), extraMatchPaths: ['/create-loan'], iconColor: 'blue' },
    { path: '/borrowers', icon: Users, label: t('nav.borrowers'), extraMatchPaths: ['/borrowers/create'], iconColor: 'green' },
    { path: '/collateral', icon: Package, label: t('nav.repledging'), extraMatchPaths: ['/collateral/create'], iconColor: 'amber' },
    { path: '/profitability', icon: TrendingUp, label: t('nav.profitability'), iconColor: 'emerald' },
    { path: '/analytics', icon: BarChart3, label: t('nav.analytics'), iconColor: 'sky' },
    { path: '/settings', icon: Settings, label: t('nav.settings'), iconColor: 'purple' },
    { path: '/support', icon: Headset, label: t('nav.support'), iconColor: 'red' },
  ];

  const getPageTitle = () => {
    const currentItem = navItems.find(item => location.pathname === item.path);
    if (currentItem) return currentItem.label;
    if (location.pathname === '/') return t('nav.dashboard');
    if (location.pathname === '/profile') return t('nav.profile');
    if (location.pathname === '/borrowers/create') return t('borrowers.createNewBorrower');
    if (location.pathname === '/create-loan') return t('common.createNewLoan');
    if (location.pathname === '/collateral') return t('nav.repledging');
    if (location.pathname === '/collateral/create') return t('collateral.createNewPledge');
    if (location.pathname.startsWith('/loans/')) return t('loans.title');
    if (location.pathname.startsWith('/borrowers/')) return t('borrowers.title');
    if (location.pathname === '/support') return t('nav.support');
    return t('nav.dashboard');
  };

  return (
    <div className="layout">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <aside className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
        <SidebarWave />
        <div className="sidebar-header">
          <div className="logo">
            <img 
              src="/LMS_SIDE_LOGO.png" 
              alt="LMS Logo" 
              style={{
                maxWidth: '100%',
                height: 'auto',
                objectFit: 'contain'
              }}
            />
          </div>
          <button
            type="button"
            className="sidebar-close-btn"
            onClick={() => setSidebarOpen(false)}
            aria-label="Close menu"
          >
            <X size={22} />
          </button>
        </div>
        
        <nav className="sidebar-nav">
          <ul className="nav-menu">
            {navItems.map((item) => {
              const Icon = item.icon;
              const matchesExtraPaths = Array.isArray(item.extraMatchPaths) && item.extraMatchPaths.some(extraPath => (
                location.pathname === extraPath || location.pathname.startsWith(`${extraPath}/`)
              ));

              const isActive = location.pathname === item.path || 
                (item.path === '/dashboard' && location.pathname === '/') ||
                (item.path !== '/dashboard' && location.pathname.startsWith(item.path + '/')) ||
                matchesExtraPaths;
              
              return (
                <li key={item.path} className="nav-item">
                  <Link 
                    to={item.path} 
                    className={`nav-link ${isActive ? 'active' : ''}`}
                    onClick={() => setSidebarOpen(false)}
                  >
                    <Icon className={`nav-icon nav-icon-${item.iconColor || 'purple'}`} />
                    <span>{item.label}</span>
                  </Link>
                </li>
              );
            })}
          </ul>
        </nav>

        <div className="sidebar-profile">
          <Link to="/profile" style={{ textDecoration: 'none' }} onClick={() => setSidebarOpen(false)}>
            <div className="sidebar-profile-card">
              {(() => {
                let userFirstName = 'User';
                let userLastName = '';
                let businessName = 'Business';
                try {
                  const user = JSON.parse(localStorage.getItem('user') || '{}');
                  userFirstName = (user.firstName || user.first_name || 'User');
                  userLastName = user.lastName || user.last_name || '';
                  const selected = JSON.parse(localStorage.getItem('selectedBusiness') || 'null');
                  const businesses = JSON.parse(localStorage.getItem('businesses') || '[]');
                  const biz = selected || businesses[0] || null;
                  if (biz && biz.businessName) businessName = biz.businessName;
                } catch (_) {}
                const truncatedBiz = businessName.length > 18 ? `${businessName.slice(0, 18)}...` : businessName;
                const initials = `${(userFirstName[0] || 'U')}${(userLastName[0] || userFirstName[1] || '').toUpperCase()}`.toUpperCase();
                const nameParts = String(userFirstName || 'User').trim().split(/\s+/).filter(Boolean);
                const firstNameWord = nameParts[0] || 'User';
                const restNameWords = nameParts.slice(1).join(' ');
                return (
                  <>
                    <div className="sidebar-profile-avatar">
                      {initials.slice(0, 2)}
                      <span className="sidebar-profile-status" />
                    </div>
                    <div className="sidebar-profile-text">
                      <div className="sidebar-profile-name">
                        <span className="sidebar-profile-greeting">
                          {t('common.hello')},{'\u00A0'}{firstNameWord}
                        </span>
                        {restNameWords ? (
                          <span className="sidebar-profile-name-rest"> {restNameWords}</span>
                        ) : null}
                      </div>
                      <div className="sidebar-profile-biz">{truncatedBiz}</div>
                    </div>
                    <ChevronRight size={16} className="sidebar-profile-chevron" />
                  </>
                );
              })()}
            </div>
          </Link>
        </div>
      </aside>

      {/* Main Content */}
      <main className="main-content">
        {/* Header */}
        <header className={`header ${headerHidden ? 'header-hidden' : ''}`}>
          <div className="header-left">
            <button
              type="button"
              className="menu-toggle-btn"
              onClick={() => setSidebarOpen((open) => !open)}
              aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
              aria-expanded={sidebarOpen}
            >
              {sidebarOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
            <h1 className="header-title">{getPageTitle()}</h1>
          </div>
          
          <div className="header-controls">
            <Link to="/create-loan" className="btn btn-primary">
              <Plus size={16} />
              <span className="btn-label">{t('common.createNewLoan')}</span>
            </Link>
            <Link 
              to="/collateral/create" 
              className="btn btn-repledge"
            >
              <Plus size={16} />
              <span className="btn-label">{t('common.createRepledging')}</span>
            </Link>
            <button 
              className="btn btn-logout"
              onClick={handleLogout}
            >
              <LogOut size={16} />
              <span className="btn-label">{t('common.logout')}</span>
            </button>
          </div>
        </header>

        {/* Page Content */}
        <div className="content" ref={contentRef}>
          {children}
        </div>

        {/* Footer */}
        <footer 
          className="footer"
          style={{
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderTop: '1px solid rgba(255, 255, 255, 0.1)',
            padding: '1.25rem 2rem',
            marginTop: 'auto',
            boxShadow: '0 -2px 8px rgba(0, 0, 0, 0.1)',
            width: '100%',
            flexShrink: 0,
            zIndex: 10
          }}
        >
          <div 
            className="footer-content"
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: '100%',
              flexWrap: 'wrap',
              gap: '0.5rem'
            }}
          >
            <span 
              className="footer-text"
              style={{
                fontSize: '0.875rem',
                color: 'rgba(255, 255, 255, 0.95)',
                textAlign: 'center',
                fontWeight: 400,
                lineHeight: '1.5'
              }}
            >
              © 2025 Loan Management. {t('common.allRightsReserved')} |{' '}
              {language === 'hi' ? (
                <>
                  <a 
                    href="https://www.seervitechlabs.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="footer-link"
                    style={{
                      color: '#ffffff',
                      textDecoration: 'none',
                      fontWeight: 600,
                      marginLeft: '0.25rem',
                      transition: 'all 0.2s ease',
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = '#fef3c7';
                      e.target.style.textDecoration = 'underline';
                      e.target.style.textShadow = '0 2px 4px rgba(0, 0, 0, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = '#ffffff';
                      e.target.style.textDecoration = 'none';
                      e.target.style.textShadow = '0 1px 2px rgba(0, 0, 0, 0.2)';
                    }}
                  >
                    Seervi Tech Labs
                  </a>
                  {' द्वारा निर्मित'}
                </>
              ) : (
                <>
                  {t('common.developedBy')}{' '}
                  <a 
                    href="https://www.seervitechlabs.com/" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="footer-link"
                    style={{
                      color: '#ffffff',
                      textDecoration: 'none',
                      fontWeight: 600,
                      transition: 'all 0.2s ease',
                      textShadow: '0 1px 2px rgba(0, 0, 0, 0.2)'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.color = '#fef3c7';
                      e.target.style.textDecoration = 'underline';
                      e.target.style.textShadow = '0 2px 4px rgba(0, 0, 0, 0.3)';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.color = '#ffffff';
                      e.target.style.textDecoration = 'none';
                      e.target.style.textShadow = '0 1px 2px rgba(0, 0, 0, 0.2)';
                    }}
                  >
                    Seervi Tech Labs
                  </a>
                </>
              )}
            </span>
          </div>
        </footer>
      </main>
    </div>
  );
};

export default Layout;
